import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { AlertTriangle } from 'lucide-react';
import { Workspace } from '../../types/common';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { deleteWorkspace } from '../../store/workspaceSlice';
import { showToast } from '../../store/uiSlice';

interface DeleteWorkspaceModalProps {
  open: boolean;
  onClose: () => void;
  workspace: Workspace | null;
}

const DeleteWorkspaceModal: React.FC<DeleteWorkspaceModalProps> = ({
  open,
  onClose,
  workspace,
}) => {
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector((state) => state.workspaces);
  const { user } = useAppSelector((state) => state.auth);

  const isOwner = workspace?.ownerId === user?.id;

  const handleDelete = async () => {
    if (!workspace || !isOwner) {
      return;
    }

    try {
      await dispatch(deleteWorkspace(workspace.id)).unwrap();

      dispatch(
        showToast({
          message: 'Workspace deleted successfully!',
          type: 'success',
        })
      );

      onClose();
    } catch (error: any) {
      dispatch(
        showToast({
          message: error.message || 'Failed to delete workspace',
          type: 'error',
        })
      );
    }
  };

  if (!workspace) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AlertTriangle size={24} color="#e44258" />
          Delete Workspace
        </Box>
      </DialogTitle>
      <DialogContent>
        {!isOwner ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            Only the workspace owner can delete this workspace.
          </Alert>
        ) : (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This action cannot be undone!
            </Alert>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to delete the workspace{' '}
              <strong>"{workspace.name}"</strong>?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Deleting this workspace will:
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2 }}>
              <Typography component="li" variant="body2" color="text.secondary">
                Remove all boards ({workspace.boardCount || 0})
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Remove all members ({workspace.memberCount || 0})
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Delete all associated data permanently
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        {isOwner && (
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Delete Workspace'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DeleteWorkspaceModal;
